/**
 * Fanuc Macro B preprocessor.
 * Evaluates variables, arithmetic, WHILE/DO loops, and canned cycles,
 * producing flat G-code that gcode-toolpath can parse.
 */

const MAX_LOOP_ITERATIONS = 1000

interface ExpandResult {
  expanded: string
  lineMap: number[]   // lineMap[expandedLineIndex] = originalSourceLine (1-based)
}

// Comparison operators used in WHILE conditions
const CMP_OPS: Record<string, (a: number, b: number) => boolean> = {
  GE: (a, b) => a >= b,
  GT: (a, b) => a > b,
  LT: (a, b) => a < b,
  LE: (a, b) => a <= b,
  EQ: (a, b) => a === b,
  NE: (a, b) => a !== b,
}

/** Evaluate a numeric expression that may contain #variables and arithmetic */
function evalExpr(expr: string, vars: Map<number, number>): number {
  // Replace #nnn with their numeric values
  let resolved = expr.replace(/#(\d+)/g, (_, num) => {
    const val = vars.get(Number(num))
    return val !== undefined ? String(val) : '0'
  })

  // Handle comparison operators → convert to JS
  resolved = resolved.replace(/\bGE\b/gi, '>=')
  resolved = resolved.replace(/\bGT\b/gi, '>')
  resolved = resolved.replace(/\bLT\b/gi, '<')
  resolved = resolved.replace(/\bLE\b/gi, '<=')
  resolved = resolved.replace(/\bEQ\b/gi, '==')
  resolved = resolved.replace(/\bNE\b/gi, '!=')

  // Strip brackets → parentheses (Fanuc uses [] for grouping)
  resolved = resolved.replace(/\[/g, '(').replace(/\]/g, ')')

  // Math functions
  resolved = resolved.replace(/\bSIN\b/gi, 'Math.sin')
  resolved = resolved.replace(/\bCOS\b/gi, 'Math.cos')
  resolved = resolved.replace(/\bTAN\b/gi, 'Math.tan')
  resolved = resolved.replace(/\bSQRT\b/gi, 'Math.sqrt')
  resolved = resolved.replace(/\bABS\b/gi, 'Math.abs')
  resolved = resolved.replace(/\bROUND\b/gi, 'Math.round')
  resolved = resolved.replace(/\bFIX\b/gi, 'Math.floor')
  resolved = resolved.replace(/\bFUP\b/gi, 'Math.ceil')

  try {
    // Safe eval — expression only contains numbers, operators, Math.*
    const fn = new Function(`return (${resolved})`)
    const result = fn()
    return typeof result === 'number' ? result : 0
  } catch {
    return 0
  }
}

/** Evaluate a WHILE condition like #100GE#101 */
function evalCondition(condition: string, vars: Map<number, number>): boolean {
  // Match patterns like: #100GE#101, #100GT0, [#100+1]LT10
  for (const [op, fn] of Object.entries(CMP_OPS)) {
    const idx = condition.toUpperCase().indexOf(op)
    if (idx !== -1) {
      const left = evalExpr(condition.slice(0, idx), vars)
      const right = evalExpr(condition.slice(idx + op.length), vars)
      return fn(left, right)
    }
  }
  // Fallback: truthy numeric check
  return evalExpr(condition, vars) !== 0
}

/** Substitute all #nnn references in a line with their values */
function substituteVars(line: string, vars: Map<number, number>): string {
  // Handle expressions in brackets: [#100+#102]
  let result = line.replace(/\[([^\]]+)\]/g, (_, expr) => {
    const val = evalExpr(expr, vars)
    return formatNumber(val)
  })

  // Handle standalone #nnn references
  result = result.replace(/#(\d+)/g, (_, num) => {
    const val = vars.get(Number(num))
    return val !== undefined ? formatNumber(val) : '0'
  })

  return result
}

/** Format number: integers stay clean, floats get up to 4 decimal places */
function formatNumber(val: number): string {
  if (Number.isInteger(val)) return String(val)
  return val.toFixed(4).replace(/0+$/, '').replace(/\.$/, '.0')
}

/** Expand a G81 drilling canned cycle into G0/G1 moves */
function expandG81(
  params: { x?: number; y?: number; z?: number; r?: number; f?: number },
): string[] {
  const lines: string[] = []
  const r = params.r ?? 2
  const z = params.z ?? -5
  const f = params.f ?? 100

  // Rapid to position
  if (params.x !== undefined || params.y !== undefined) {
    const parts = ['G0']
    if (params.x !== undefined) parts.push(`X${formatNumber(params.x)}`)
    if (params.y !== undefined) parts.push(`Y${formatNumber(params.y)}`)
    lines.push(parts.join(' '))
  }
  // Rapid to R plane
  lines.push(`G0 Z${formatNumber(r)}`)
  // Feed to depth
  lines.push(`G1 Z${formatNumber(z)} F${formatNumber(f)}`)
  // Rapid retract to R plane
  lines.push(`G0 Z${formatNumber(r)}`)
  return lines
}

/** Expand a G83 peck drilling canned cycle into G0/G1 moves */
function expandG83(
  params: { x?: number; y?: number; z?: number; r?: number; q?: number; f?: number },
): string[] {
  const lines: string[] = []
  const r = params.r ?? 2
  const z = params.z ?? -10
  const q = params.q ?? 2
  const f = params.f ?? 100

  // Rapid to position
  if (params.x !== undefined || params.y !== undefined) {
    const parts = ['G0']
    if (params.x !== undefined) parts.push(`X${formatNumber(params.x)}`)
    if (params.y !== undefined) parts.push(`Y${formatNumber(params.y)}`)
    lines.push(parts.join(' '))
  }
  // Rapid to R plane
  lines.push(`G0 Z${formatNumber(r)}`)

  // Peck drill loop
  let currentDepth = r
  const totalDepth = z
  while (currentDepth > totalDepth) {
    const nextDepth = Math.max(currentDepth - q, totalDepth)
    // Feed to next peck depth
    lines.push(`G1 Z${formatNumber(nextDepth)} F${formatNumber(f)}`)
    // Rapid retract to R plane
    lines.push(`G0 Z${formatNumber(r)}`)
    // Rapid back to just above previous depth (if not done)
    if (nextDepth > totalDepth) {
      lines.push(`G0 Z${formatNumber(nextDepth + 0.5)}`)
    }
    currentDepth = nextDepth
  }
  // Final retract
  lines.push(`G0 Z${formatNumber(r)}`)
  return lines
}

/** Parse canned cycle parameters from a line like G81 X10 Y20 Z-5 R2 F100 */
function parseCannedParams(line: string): Record<string, number> {
  const params: Record<string, number> = {}
  const matches = line.matchAll(/([XYZRQFIJK])(-?[\d.]+)/gi)
  for (const m of matches) {
    params[m[1].toLowerCase()] = parseFloat(m[2])
  }
  return params
}

export function expandMacros(gcode: string, sharedVars?: Map<number, number>): ExpandResult {
  const sourceLines = gcode.split('\n')
  const expandedLines: string[] = []
  const lineMap: number[] = []
  const vars = sharedVars ?? new Map<number, number>()

  let i = 0
  while (i < sourceLines.length) {
    const rawLine = sourceLines[i]
    const trimmed = rawLine.trim()
    const srcLineNum = i + 1  // 1-based

    // Skip empty lines and comments — pass through
    if (!trimmed || trimmed.startsWith(';') || trimmed.startsWith('(') || trimmed.startsWith('%')) {
      expandedLines.push(rawLine)
      lineMap.push(srcLineNum)
      i++
      continue
    }

    // Strip optional N-word line number prefix (e.g. "N100 ..." → "...")
    const stripped = trimmed.replace(/^N\d+\s*/i, '')
    const upper = stripped.toUpperCase()

    // Variable assignment: #100=-2.0 or #100=[#100+#102]
    const assignMatch = stripped.match(/^#(\d+)\s*=\s*(.+)$/i)
    if (assignMatch) {
      const varNum = Number(assignMatch[1])
      const val = evalExpr(assignMatch[2], vars)
      vars.set(varNum, val)
      // Don't emit variable assignments to output
      i++
      continue
    }

    // WHILE/DO loop: WHILE[condition]DOn
    const whileMatch = upper.match(/^WHILE\s*\[(.+?)\]\s*DO\s*(\d+)/)
    if (whileMatch) {
      const condition = whileMatch[1]
      const loopId = whileMatch[2]
      const endPattern = new RegExp(`^END\\s*${loopId}\\s*$`, 'i')

      // Find matching END (strip N-word prefix from each line)
      let endIdx = -1
      let depth = 0
      for (let j = i + 1; j < sourceLines.length; j++) {
        const jStripped = sourceLines[j].trim().replace(/^N\d+\s*/i, '').toUpperCase()
        if (jStripped.match(/^WHILE\s*\[.*?\]\s*DO\s*\d+/)) depth++
        if (jStripped.match(endPattern) && depth === 0) {
          endIdx = j
          break
        }
        if (jStripped.match(/^END\s*\d+\s*$/) && depth > 0) depth--
      }

      if (endIdx === -1) {
        // Can't find END — pass through as-is
        expandedLines.push(rawLine)
        lineMap.push(srcLineNum)
        i++
        continue
      }

      // Extract loop body lines
      const bodySourceLines = sourceLines.slice(i + 1, endIdx)

      // Execute loop
      let iterations = 0
      while (evalCondition(condition, vars) && iterations < MAX_LOOP_ITERATIONS) {
        // Recursively expand the body
        const bodyResult = expandMacros(bodySourceLines.join('\n'), vars)
        for (let k = 0; k < bodyResult.expanded.split('\n').length; k++) {
          const bodyLine = bodyResult.expanded.split('\n')[k]
          if (bodyLine.trim()) {
            expandedLines.push(bodyLine)
            // Map back to original source line within loop body
            const bodyOrigLine = bodyResult.lineMap[k]
            lineMap.push(bodyOrigLine !== undefined ? (i + 1) + bodyOrigLine : srcLineNum)
          }
        }
        iterations++
      }

      i = endIdx + 1
      continue
    }

    // Canned cycle G81
    if (upper.match(/^G81\b/)) {
      const substituted = substituteVars(trimmed, vars)
      const params = parseCannedParams(substituted)
      const expanded = expandG81(params)
      for (const eline of expanded) {
        expandedLines.push(eline)
        lineMap.push(srcLineNum)
      }
      i++
      continue
    }

    // Canned cycle G83
    if (upper.match(/^G83\b/)) {
      const substituted = substituteVars(trimmed, vars)
      const params = parseCannedParams(substituted)
      const expanded = expandG83(params)
      for (const eline of expanded) {
        expandedLines.push(eline)
        lineMap.push(srcLineNum)
      }
      i++
      continue
    }

    // G80 — cancel canned cycle, pass through
    if (upper.match(/^G80\b/)) {
      expandedLines.push(trimmed)
      lineMap.push(srcLineNum)
      i++
      continue
    }

    // Regular line — substitute variables and pass through
    const substituted = substituteVars(trimmed, vars)
    expandedLines.push(substituted)
    lineMap.push(srcLineNum)
    i++
  }

  return {
    expanded: expandedLines.join('\n'),
    lineMap,
  }
}
